import React from 'react'
import PropTypes from 'prop-types'
import ActionModal from './ActionModal'
import { useTransition } from 'react-spring'
import { Modal } from '@entrata/portals-common/lib/components'
import { X } from '@entrata/portals-common/lib/icons'
import {
  StyledModalDiv,
  StyledTitle,
  StyledWrapper,
  portalStyle,
  rootStyle,
} from './rp-modal'
import Media from 'react-media'
import styled from '@emotion/styled'

import DangerousInnerHTML from 'portal-modules/common/components/DangerousInnerHTML'
import { underlineFocus, FocusSpan } from '../focus-styles'
import queryString from 'query-string'
import { getFormattedDateTime } from 'portal-modules/common/utils/get-formatted-date-time'

const StyledButton = styled('button')`
  background-color: transparent;
  border: none;
  color: ${({ theme }) => theme.secondary.base};
  padding: 4px;
  transition: background 250ms ease-in-out;
  ${underlineFocus};
`
const StyledDiv = styled('div')`
  padding: 0 16px 16px 16px;
`
const StyledDivLargeBtn = styled('div')`
  padding: 0 16px 16px 16px;
  display: flex;
  justify-content: center;
`

const StyledDivInner = styled('div')`
  margin-top: 8px;
`
const ModStyledWrapper = styled(StyledWrapper)`
  width: 90vw;
  max-width: 960px;
  height: auto;
  display: flex;
`
const ModStyledModalDiv = styled(StyledModalDiv)`
  height: auto;
  &.remove-pad {
    padding-bottom: 0;
  }
`
const ModStyledTitle = styled(StyledTitle)`
  align-items: center;
  display: flex;
  font-size: 16px;
  justify-content: space-between;
`
const StyledFigure = styled('figure')`
  overflow: hidden;
  text-align: center;
  max-width: 100%;
  width: ${({ width }) => width}px;
  margin-left: auto;
  margin-right: auto;

  img {
    max-width: 280px;
    @media screen and (min-width: 768px) {
      max-width: 512px;
    }
  }
`
const StyledDescriptionP = styled('p')`
  font-size: 13px;
  word-wrap: break-word;
  margin: 16px;

  @media (min-width: 768px) {
    margin-bottom: 32px;
  }
  @media (min-width: 1024px) {
    margin: 0 0 32px 16px;
  }

  img {
    max-width: 280px;
    @media screen and (min-width: 768px) {
      max-width: 512px;
    }
  }
`
export const StyledDate = styled('span')`
  color: ${({ theme }) => theme.text.dark};
  font-size: 10px;
  margin-left: 16px;

  font-weight: bold;
  @media screen and (min-width: 768px) {
    font-size: 11px;
    font-weight: bold;
  }
  @media screen and (min-width: 1024px) {
    font-size: 12px;
    font-weight: bold;
    margin-left: 16px;
  }
`

const StyledDangerousInnerHTML = styled(DangerousInnerHTML)`
  text-align: ${({ isImage }) => (isImage ? 'center' : 'left')};
`

const CommunityRequestContentModal = ({
  blurId,
  buttonLabel,
  children,
  hideCloseButton,
  onClose,
  note,
  image_url,
  rootId,
  start_date,
  end_date,
  show,
  title,
  propertyLocaleCode,
}) => {
  const toggleRoot = action => {
    const rootElement = document.getElementById(rootId)
    const blurElement = document.getElementById(blurId)
    rootElement && rootElement.classList[action](rootStyle)
    blurElement && blurElement.classList[action]('blur')
  }

  React.useEffect(() => {
    toggleRoot(show ? 'add' : 'remove')
    return function removeRootStyles() {
      toggleRoot('remove')
    }
  }, [show])

  const transitions = useTransition(show, null, {
    from: { opacity: 0, transform: 'translateY(100px)' },
    enter: { opacity: 1, transform: 'translateY(0px)' },
    leave: { opacity: 0, transform: 'translateY(-200px)' },
  })
  return transitions.map(
    ({ item, key, props }) =>
      item && (
        <Modal className={portalStyle} key={key} onClose={onClose}>
          {({ containerProps, titleId }) => (
            <ModStyledWrapper style={{ ...props }}>
              <ModStyledModalDiv
                className={!hideCloseButton ? 'remove-pad' : ''}
                {...containerProps}
              >
                <ModStyledTitle id={titleId}>
                  {title || __('Title')}
                  <StyledButton onClick={onClose}>
                    <FocusSpan>
                      <X />
                    </FocusSpan>
                  </StyledButton>
                </ModStyledTitle>
                {children}

                <StyledDivInner>
                  {image_url ? (
                    <StyledFigure
                      width={queryString.parse(image_url).w}
                      height={queryString.parse(image_url).h}
                    >
                      <img src={image_url} />{' '}
                    </StyledFigure>
                  ) : (
                    ''
                  )}
                  <StyledDate>
                    <time dateTime={start_date}>
                      {getFormattedDateTime({
                        dateTime: start_date,
                        isYearRequired: true,
                        propertyLocaleCode,
                      })}
                    </time>
                  </StyledDate>
                  <StyledDangerousInnerHTML
                    component={StyledDescriptionP}
                    isImage={note.includes('<img')}
                  >
                    {note}
                  </StyledDangerousInnerHTML>
                </StyledDivInner>
                {!hideCloseButton && (
                  <StyledDivLargeBtn>
                    <Media query='(min-width: 768px)'>
                      {largeScreen => (
                        <ActionModal.Button
                          size='large'
                          onClick={onClose}
                          slim={largeScreen}
                          style={{ marginTop: '16px' }}
                        >
                          {buttonLabel}
                        </ActionModal.Button>
                      )}
                    </Media>
                  </StyledDivLargeBtn>
                )}
              </ModStyledModalDiv>
            </ModStyledWrapper>
          )}
        </Modal>
      ),
  )
}

CommunityRequestContentModal.propTypes = {
  /** ID of the background to be blurred  */
  blurId: PropTypes.string,
  /** Custom text for close button */
  buttonLabel: PropTypes.string,
  /** You can directly pass some string content to show to the users */
  children: PropTypes.any,
  /** To toggle visibility of Close button */
  hideCloseButton: PropTypes.bool,
  /** onClose method, provided by the modal control */
  onClose: PropTypes.func.isRequired,
  /** ID of the root element */
  rootId: PropTypes.string,
  /** A flas to show/hide modal. Provided by modal control */
  show: PropTypes.bool,
  /** Title of the modal  */
  title: PropTypes.string,
}
CommunityRequestContentModal.defaultProps = {
  blurId: 'bg-img',
  buttonLabel: __('Close'),
  rootId: 'root',
}

export default CommunityRequestContentModal
